import { useState } from 'react';
import { Alert, App, Button, Card, Radio, Space, Tag, Typography } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';

export type PaymentMode = 'RAZORPAY_ONLY' | 'USER_CHOICE_BILLING';

export const PAYMENT_MODE_LABEL: Record<PaymentMode, string> = {
  RAZORPAY_ONLY: 'Razorpay Only',
  USER_CHOICE_BILLING: 'User Choice Billing',
};

function errMsg(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? fallback;
}

/**
 * Which payment flow NEW recharges use. One value on the server, so both can
 * never be on at once. Orders already started finish with the provider they
 * started with, whatever this is switched to.
 */
export function PaymentModeCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { data } = useQuery({
    queryKey: ['admin', 'payment-config'],
    queryFn: async () => (await api.get<{ paymentMode: PaymentMode }>('/admin/payment-config')).data,
  });
  const [draft, setDraft] = useState<PaymentMode | null>(null);

  const save = useMutation({
    mutationFn: async (paymentMode: PaymentMode) => api.put('/admin/payment-config', { paymentMode }),
    onSuccess: () => {
      message.success('Payment method updated');
      setDraft(null);
      void qc.invalidateQueries({ queryKey: ['admin', 'payment-config'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not save payment method')),
  });

  if (!data) return null;
  const value = draft ?? data.paymentMode;

  return (
    <Card
      title={
        <Space>
          Payment method
          <Tag color="green" data-testid="active-payment-mode">
            Active: {PAYMENT_MODE_LABEL[data.paymentMode]}
          </Tag>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Radio.Group value={value} onChange={(e) => setDraft(e.target.value as PaymentMode)}>
          <Space direction="vertical">
            <Radio value="RAZORPAY_ONLY">
              <b>Razorpay Only</b>
              <Typography.Text type="secondary"> — Use Razorpay as the payment method for recharge.</Typography.Text>
            </Radio>
            <Radio value="USER_CHOICE_BILLING">
              <b>User Choice Billing</b>
              <Typography.Text type="secondary">
                {' '}
                — Allow the app&rsquo;s User Choice Billing payment flow (Google&rsquo;s chooser: Google
                Play or Razorpay).
              </Typography.Text>
            </Radio>
          </Space>
        </Radio.Group>
        {value === 'RAZORPAY_ONLY' && (
          <Alert
            type="warning"
            showIcon
            message="Google Play policy"
            description="For Play-installed apps, Google Play's payments policy requires Play Billing (or its User Choice Billing chooser) for coins sold to users in India. Razorpay Only skips Google Play entirely."
          />
        )}
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          Applies to every new recharge as soon as you save. The server enforces it, so an old or
          modified app cannot start the other flow. Payments already in progress still complete and
          credit normally, and payment history is never changed.
        </Typography.Paragraph>
        <Can permission="payment_mode.edit">
          <Button
            type="primary"
            disabled={draft === null || draft === data.paymentMode}
            loading={save.isPending}
            onClick={() => draft !== null && save.mutate(draft)}
          >
            Save
          </Button>
        </Can>
      </Space>
    </Card>
  );
}
